import type { JSX } from 'react';
import { TabBar } from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { ServiceCode } from '@common/enums';
import { OutclinicDashboard } from '@frontend/components/pages/outclinicDashboard/OutclinicDashboard';
import { OutclinicDashboardTemplate } from '@frontend/components/pages/outclinicDashboard/OutclinicDashboardTemplate';
import { getServiceEmploymentSettings } from '@frontend/services/api/serviceEmploymentsApiService';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { useNavigateInTabBar } from '@frontend/utils/hooks/useNavigateInTabBar';

type Service = {
    code: ServiceCode;
    label: string;
};

export function OsasOutclinicDashboard(): JSX.Element {
    const abortSignal = useAbortSignal();
    const [services, setServices] = useState<Service[]>([]);
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const { selectedTab, setSelectedTab } = useNavigateInTabBar();
    const serviceCode = useMemo(getServiceCode, [selectedTab]);

    useEffect(() => {
        loadServices();
    }, []);

    return (
        <OutclinicDashboardTemplate title="Dashboard Slaap">
            <>
                {services.length > 1 && (
                    <TabBar>
                        {services.map(({ code, label }) => (
                            <TabBar.Button
                                isSelected={serviceCode === code}
                                key={code}
                                onClick={() => setSelectedTab(code)}
                            >
                                {label}
                            </TabBar.Button>
                        ))}
                    </TabBar>
                )}
                {services.length > 0 && (
                    <OutclinicDashboard
                        key={serviceCode}
                        serviceCode={serviceCode}
                    />
                )}
            </>
        </OutclinicDashboardTemplate>
    );

    function getServiceCode(): ServiceCode {
        return (selectedTab as ServiceCode) || ServiceCode.OSAS;
    }

    async function loadServices(): Promise<void> {
        const osasSettings = await getServiceEmploymentSettings(selectedEmploymentId, ServiceCode.OSAS, abortSignal);
        const authorizedServices = osasSettings?.canReadOsasDashboard
            ? [{ code: ServiceCode.OSAS, label: 'Slaap' }]
            : [];
        setServices(authorizedServices);
    }
}
